import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, Trophy, Users, History, Loader2, Brain } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScoreComparisonWidget, CommunityStatsWidget } from '../components/EngagementWidgets';
import { MilestonesWidget } from '../components/MilestoneCelebration';
import BottomNav from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

/**
 * Progress page — consolidates milestones, community stats, score comparison,
 * and assessment history behind a proper route so the Dashboard home can stay
 * focused on "what to do right now".
 */
export default function ProgressPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth?redirect=/progress');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/assessments`, { headers: getAuthHeader() });
        setAssessments(Array.isArray(res.data?.assessments) ? res.data.assessments : []);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [user, getAuthHeader]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#FAF8F3]/95 backdrop-blur border-b border-border/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center"
            aria-label="Back to dashboard"
            data-testid="progress-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#1A1A1A]">Your Progress</h1>
            <p className="text-xs text-muted-foreground">Milestones · Community · Assessment history</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Milestones */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-3 px-1">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h2 className="font-semibold text-sm text-[#1A1A1A]">Milestones</h2>
          </div>
          <MilestonesWidget getAuthHeader={getAuthHeader} />
        </motion.section>

        {/* Score comparison + community, side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h2 className="font-semibold text-sm text-[#1A1A1A]">Score progression</h2>
            </div>
            <ScoreComparisonWidget getAuthHeader={getAuthHeader} />
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <Users className="w-4 h-4 text-indigo-600" />
              <h2 className="font-semibold text-sm text-[#1A1A1A]">Community</h2>
            </div>
            <CommunityStatsWidget getAuthHeader={getAuthHeader} />
          </motion.section>
        </div>

        {/* Assessment history */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-3 px-1">
            <History className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-semibold text-sm text-[#1A1A1A]">Assessment history</h2>
          </div>
          {loading ? (
            <div className="bg-white rounded-2xl border border-border p-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : assessments.length === 0 ? (
            <div className="bg-white rounded-2xl border border-border p-6 text-center text-sm text-muted-foreground">
              No assessments yet. Take your first quiz from the dashboard.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-border divide-y divide-border overflow-hidden">
              {assessments.map((a, i) => {
                const suppCount = (a.recommended_supplements || []).filter(s => !s.is_medication).length;
                const medCount = (a.medications || []).length;
                return (
                  <button
                    key={a.id || i}
                    onClick={() => navigate(`/results/${a.id}`)}
                    className="w-full text-left p-4 hover:bg-muted/40 transition-colors flex items-center gap-3"
                    data-testid={`progress-assessment-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1A1A1A]">{formatDate(a.created_at)}</p>
                      <p className="text-xs text-muted-foreground">
                        {suppCount} supp{medCount > 0 && ` · ${medCount} med${medCount !== 1 ? 's' : ''}`}
                        {i === 0 && <span className="ml-2 text-emerald-600 font-medium">Current</span>}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </motion.section>
      </div>

      <BottomNav />
    </div>
  );
}
